import React, { useState, useEffect, useCallback, useRef } from 'react';
import { metersService } from '../services/api';
import Pagination from '../components/Pagination';
import {
  Satellite, Eye, DollarSign, AlertCircle, Zap, User, Loader,
  X, Check, Copy, Lock, Unlock, Radio, Wifi, WifiOff, Link2,
  RefreshCw, Pencil, Info, History, Filter, Printer, Download,
  TrendingUp, FileText, Send, Calendar, ChevronDown, ChevronUp,
  Settings, ToggleLeft,
  Activity, Gauge, Battery, DoorOpen, DoorClosed, ShieldCheck,
  ShieldAlert, Cpu, Droplet, BarChart3
} from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import { PermissionGuard } from '../components/PermissionGuard';
import { PERMISSIONS } from '../utils/permissions';


// ─── couleurs par type de tuile ─────────────────────────────────────────────
const COLOR_STYLES = {
  blue: { border: 'hover:border-blue-500', bg: 'bg-blue-50', text: 'text-blue-600' },
  green: { border: 'hover:border-green-500', bg: 'bg-green-50', text: 'text-green-600' },
  orange: { border: 'hover:border-orange-500', bg: 'bg-orange-50', text: 'text-orange-600' },
  red: { border: 'hover:border-red-500', bg: 'bg-red-50', text: 'text-red-600' },
  blue: { border: 'hover:border-blue-500', bg: 'bg-blue-50', text: 'text-blue-600' },
  teal: { border: 'hover:border-teal-500', bg: 'bg-teal-50', text: 'text-teal-600' },
  indigo: { border: 'hover:border-indigo-500', bg: 'bg-indigo-50', text: 'text-indigo-600' },
  gray: { border: 'hover:border-gray-400', bg: 'bg-gray-100', text: 'text-gray-600' },
};

const ActionCard = ({ icon: Icon, title, color, onClick, disabled = false, loading = false }) => {
  const s = COLOR_STYLES[color] || COLOR_STYLES.blue;
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      title={title}
      className={`relative group bg-white rounded-xl shadow-sm border-2 border-transparent 
        ${s.border} hover:shadow-lg transition-all p-4 text-center
        flex flex-col items-center justify-start
        min-h-[140px]
        disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      <div className={`w-14 h-14 mb-2 rounded-full ${s.bg} flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform`}>
        {loading ? <Loader className={`w-7 h-7 ${s.text} animate-spin`} /> : <Icon className={`w-7 h-7 ${s.text}`} />}
      </div>
      <h3 className="text-xs sm:text-sm font-semibold text-gray-900 leading-snug break-words">
        {title}
      </h3>
    </button>
  );
};

// ─── opération → libellé lisible ────────────────────────────────────────────
const OP_LABELS = {
  remotely_read: 'Relevé à distance',
  clear_credit_remotely: 'Effacer crédit (distance)',
  clear_tamper_remotely: 'Effacer sabotage (distance)',
  vending_meter_remotely: 'Recharge à distance',
  block_meter: 'Blocage compteur',
  unblock_meter: 'Déblocage compteur',
  update_ami_meter: 'Association DCU',
  remotely_set_concentrator: 'Reconfiguration DCU',
  vending_send_token: 'Renvoi token',
  switch_to_prepaid: 'Bascule prépayé',
  switch_to_postpaid: 'Bascule postpayé',
  query_daily_consumption: 'Consommation journalière',
};

// ─── carte stat pour affichage compteur (voltage, power, etc.) ────────────
const STAT_COLORS = {
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600', text: 'text-blue-900' },
  yellow: { bg: 'bg-yellow-50', border: 'border-yellow-200', icon: 'text-yellow-600', text: 'text-yellow-900' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'text-orange-600', text: 'text-orange-900' },
  green: { bg: 'bg-green-50', border: 'border-green-200', icon: 'text-green-600', text: 'text-green-900' },
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600', text: 'text-blue-900' },
  red: { bg: 'bg-red-50', border: 'border-red-200', icon: 'text-red-600', text: 'text-red-900' },
  gray: { bg: 'bg-gray-50', border: 'border-gray-200', icon: 'text-gray-600', text: 'text-gray-900' },
};

const StatCard = ({ label, value, color = 'blue' }) => {
  const c = STAT_COLORS[color] || STAT_COLORS.blue;
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className={`rounded-xl border ${c.border} ${c.bg} p-3`}>
      <p className="text-xs text-gray-500 font-medium mb-1">{label}</p>
      <p className={`text-base font-bold ${c.text} break-words`}>{value}</p>
    </div>
  );
};

// ─── badge d'état (relais, capot, ami...) ───────────────────────────────────
const StateBadge = ({ label, value, onLabel = 'ON', offLabel = 'OFF' }) => {
  if (value === null || value === undefined || value === '') return null;
  const isOn = ['1', 'on', 'true', 'ouvert', 'open'].includes(String(value).toLowerCase());
  return (
    <div className={`rounded-xl border p-3 ${isOn ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
      <p className="text-xs text-gray-500 font-medium mb-1">{label}</p>
      <p className={`text-sm font-bold ${isOn ? 'text-green-700' : 'text-red-700'}`}>
        {isOn ? onLabel : offLabel} <span className="text-gray-400 font-normal">({value})</span>
      </p>
    </div>
  );
};
// ─── carte complète pour un relevé QueryDailySinglePhaseMeter ──────────────
const DailyMeterReadingCard = ({ data }) => {
  const formatDateHeure = (raw) => {
    if (!raw) return '—';
    const parts = raw.split('-');
    if (parts.length === 4) {
      const [y, m, d, h] = parts;
      return `${d}/${m}/${y} à ${h}h00`;
    }
    return raw;
  };

  return (
    <div className="bg-white rounded-2xl border-2 border-blue-100 overflow-hidden shadow-sm">
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-white">
          <Calendar className="w-4 h-4" />
          <span className="font-semibold text-sm">{formatDateHeure(data.Date)}</span>
        </div>
        {data.Concentrator_id && (
          <span className="text-xs text-blue-100 font-mono">DCU: {data.Concentrator_id}</span>
        )}
      </div>

      <div className="p-4 space-y-4">
        <div>
          <p className="text-xs font-bold text-gray-400 uppercase mb-2">⚡ Mesures électriques</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <StatCard label="Tension" value={data.Voltage} color="blue" />
            <StatCard label="Courant" value={data.Current} color="blue" />
            <StatCard label="Puissance" value={data.Power} color="blue" />
          </div>
        </div>

        <div>
          <p className="text-xs font-bold text-gray-400 uppercase mb-2">💰 Crédit & Consommation</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <StatCard label="Total acheté" value={data.Cumulative_Purchased_Amount} color="blue" />
            <StatCard label="Solde restant" value={data.Residual_Amount} color="blue" />
            <StatCard label="Conso. totale" value={data.Cumulative_Total_Consumption} color="blue" />
          </div>
        </div>

        <div>
          <p className="text-xs font-bold text-gray-400 uppercase mb-2">🔌 États du compteur</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StateBadge label="Relais" value={data.RelayState} onLabel="Fermé (actif)" offLabel="Ouvert (coupé)" />
            <StateBadge label="Capot" value={data.CoverState} onLabel="Ouvert ⚠️" offLabel="Fermé" />
            <StateBadge label="État AMI" value={data.AmiState} onLabel="Actif" offLabel="Inactif" />
          </div>
        </div>

        {data.MeterType && (
          <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
            <Cpu className="w-4 h-4 text-gray-400" />
            <span className="text-xs text-gray-500">Type de compteur : <span className="font-semibold text-gray-700">{data.MeterType}</span></span>
          </div>
        )}
      </div>
    </div>
  );
};

export default function RemoteControlPage() {
  // ── compteur sélectionné
  const [meterId, setMeterId] = useState('');
  const [meterInfo, setMeterInfo] = useState(null);   // infos STRON

  // ── DCU
  const [dcuId, setDcuId] = useState('');
  const [dcuInput, setDcuInput] = useState('');
  const [editingDcu, setEditingDcu] = useState(false);
  const [loadingDcu, setLoadingDcu] = useState(false);
  const [dcuOnline, setDcuOnline] = useState(null);
  const [dcuDetails, setDcuDetails] = useState(null);

  // ── état loaders
  const [loadingAction, setLoadingAction] = useState(null);

  // ── feedback
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // ── statut compteur (bloqué/actif, dernière opération)
  const [meterBlocked, setMeterBlocked] = useState(null);

  const [showFullInfoModal, setShowFullInfoModal] = useState(false);
  const [fullInfoData, setFullInfoData] = useState(null);
  const { hasPermission } = usePermissions();

  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const elapsedRef = useRef(null);

  const startElapsedTimer = () => {
    setElapsedSeconds(0);
    elapsedRef.current = setInterval(() => setElapsedSeconds(s => s + 1), 1000);
  };
  const stopElapsedTimer = () => {
    clearInterval(elapsedRef.current);
    setElapsedSeconds(0);
  };

  // ── historique local
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilter, setHistoryFilter] = useState({ meter_id: '', status: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 8;
  const paginatedHistory = history.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const totalPages = Math.ceil(history.length / ITEMS_PER_PAGE);

  // ── modals
  const [showReadingModal, setShowReadingModal] = useState(false);
  const [readingData, setReadingData] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [showDcuModal, setShowDcuModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [creditData, setCreditData] = useState(null);
  const [showConsoModal, setShowConsoModal] = useState(false);
  const [consoData, setConsoData] = useState(null);
  const [consoDate, setConsoDate] = useState('');
  const [consoPhase, setConsoPhase] = useState('single');
  const [showSendTokenModal, setShowSendTokenModal] = useState(false);
  const [sendTokenVal, setSendTokenVal] = useState('');
  const [showSwitchModal, setShowSwitchModal] = useState(false);
  const [showVendingModal, setShowVendingModal] = useState(false);
  const [vendAmount, setVendAmount] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedOp, setSelectedOp] = useState(null);

  // ── ✅ NOUVEAU : modal Token (même comportement que le Centre Avancé)
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [tokenData, setTokenData] = useState(null);
  const tokenRef = useRef(null);

  const resetMsg = () => { setError(''); setSuccess(''); };

  // ── ✅ NOUVEAU : gère uniformément le résultat d'une opération.
  // Si la réponse STRON contient un token, on ouvre le modal Token (copier/imprimer/télécharger).
  // Sinon on affiche juste le message de succès classique.
  const handleActionResult = (res, actionLabel) => {
    if (res?.data?.token) {
      setTokenData({
        token: res.data.token,
        action: actionLabel,
        meter_id: meterId,
        generation_time: res.data.generation_time || new Date().toLocaleString('fr-FR'),
        amount: res.data.amount,
        message: res.data.message,
      });
      setShowTokenModal(true);
    } else {
      setSuccess(`✅ ${res?.data?.message || actionLabel + ' réussi'}`);
    }
  };

  const copyToken = () => {
    if (tokenData?.token) {
      navigator.clipboard.writeText(tokenData.token);
      setSuccess('✅ Token copié dans le presse-papier !');
    }
  };

  const printToken = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Token - ${tokenData?.meter_id}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; max-width: 600px; margin: 0 auto; }
            .header { text-align: center; border-bottom: 3px solid #4f46e5; padding-bottom: 20px; margin-bottom: 30px; }
            .token-box { background: #f3f4f6; border: 2px dashed #4f46e5; padding: 30px; text-align: center; 
                        font-size: 28px; font-weight: bold; letter-spacing: 3px; margin: 30px 0; 
                        font-family: 'Courier New', monospace; }
            .info { margin: 15px 0; padding: 10px; background: #f9fafb; border-left: 4px solid #4f46e5; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; 
                     text-align: center; font-size: 12px; color: #6b7280; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>🎫 TOKEN</h1>
            <p>Contrôle à Distance</p>
          </div>
          <div class="info"><strong>📟 Compteur:</strong> ${tokenData?.meter_id}</div>
          <div class="info"><strong>⚡ Action:</strong> ${tokenData?.action}</div>
          ${tokenData?.amount ? `<div class="info"><strong>💰 Montant:</strong> ${tokenData.amount} kWh</div>` : ''}
          <div class="token-box">${tokenData?.token}</div>
          <div class="info"><strong>🕒 Généré le:</strong> ${tokenData?.generation_time}</div>
          ${tokenData?.message ? `<div class="info"><strong>📝 Note:</strong> ${tokenData.message}</div>` : ''}
          <div class="footer">
            <p>⚠️ Ce token est à usage unique. Conservez-le en lieu sûr.</p>
            <p>Document généré automatiquement - ${new Date().toLocaleString('fr-FR')}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const downloadToken = () => {
    const content = `
═══════════════════════════════════════════
              TOKEN - COMPTEUR
═══════════════════════════════════════════
📟 Compteur: ${tokenData?.meter_id}
⚡ Action: ${tokenData?.action}
${tokenData?.amount ? `💰 Montant: ${tokenData.amount} kWh` : ''}
🎫 TOKEN: ${tokenData?.token}
🕒 Généré le: ${tokenData?.generation_time}
${tokenData?.message ? `📝 Note: ${tokenData.message}` : ''}
═══════════════════════════════════════════
⚠️ Ce token est à usage unique
═══════════════════════════════════════════
    `.trim();
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `token_${tokenData?.meter_id}_${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ── charger historique
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const p = new URLSearchParams();
      if (historyFilter.meter_id) p.append('meter_id', historyFilter.meter_id);
      if (historyFilter.status) p.append('status', historyFilter.status);
      // opérations "remote" uniquement
      const res = await metersService.getHistory(p.toString());
      const remoteOps = [
        'remotely_read', 'clear_credit_remotely', 'clear_tamper_remotely',
        'vending_meter_remotely', 'block_meter', 'unblock_meter',
        'update_ami_meter', 'remotely_set_concentrator', 'vending_send_token',
        'switch_to_prepaid', 'switch_to_postpaid'
      ];
      setHistory(res.data.filter(o => remoteOps.includes(o.operation_type)));
      setCurrentPage(1);
    } catch (e) { console.error(e); }
    finally { setHistoryLoading(false); }
  }, [historyFilter]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // ── quand on tape un ID de compteur → chercher le DCU en base
  // ✅ Debounce élargi (800ms) + on attend un numéro plausible (>=8 chiffres)
  // pour éviter de spammer /meter-concentrator/xxx à chaque frappe (voir logs 404 en rafale)
  const checkDcu = useCallback(async (id) => {
    if (!id) return;
    setLoadingDcu(true);
    setDcuId('');
    setDcuOnline(null);
    setEditingDcu(false);
    try {
      const res = await metersService.getMeterConcentrator(id);
      const dcu = res?.data?.concentrator_id;
      if (dcu) {
        setDcuId(dcu);
        // vérifier s'il est en ligne
        try {
          const info = await metersService.queryConcentratorInfo(dcu);
          if (info?.data?.success) {
            const on = String(info.data.info?.Online).toLowerCase() === 'true' || info.data.info?.Online === '1';
            setDcuOnline(on);
            setDcuDetails(info.data.info);
          }
        } catch { }
      }
    } catch { }
    finally { setLoadingDcu(false); }
  }, []);

  useEffect(() => {
    if (!meterId || meterId.length < 8) {
      setDcuId(''); setDcuOnline(null); setMeterBlocked(null); setMeterInfo(null);
      return;
    }
    const t = setTimeout(() => checkDcu(meterId), 800);
    return () => clearTimeout(t);
  }, [meterId, checkDcu]);

  // ── associer DCU
  const handleAssociateDcu = async () => {
    if (!meterId || !dcuInput) { setError('Entrez un ID de concentrateur'); return; }
    resetMsg();
    setLoadingAction('dcu');
    try {
      const res = await metersService.updateAmiMeter(meterId, dcuInput);
      if (!res?.data?.success) { setError(res?.data?.message || 'Erreur association DCU'); return; }
      setSuccess(`Compteur ${meterId} associé au DCU ${dcuInput}`);
      setDcuInput('');
      setEditingDcu(false);
      await checkDcu(meterId);
      loadHistory();
    } catch (e) { setError(e?.response?.data?.detail?.message || 'DCU introuvable dans STRON'); }
    finally { setLoadingAction(null); }
  };

  // ── garde-fou : DCU obligatoire ET en ligne pour les opérations réseau
  // ✅ Bloque désormais l'action si le DCU est connu hors ligne, au lieu de laisser
  // l'utilisateur attendre 20-60s pour un échec certain (voir logs "doesn't online" / Timeout)
  const needDcu = () => {
    if (!dcuId) { setError("Aucun concentrateur associé à ce compteur. Associez-le d'abord."); return false; }
    if (dcuOnline === false) {
      setError("⚠️ Le concentrateur (DCU) est actuellement hors ligne. L'opération échouerait après un long délai d'attente. Vérifiez le DCU (bouton 🔄) avant de réessayer.");
      return false;
    }
    return true;
  };

  const handleFullInfo = async () => {
    if (!meterId) { setError('Entrez un numéro de compteur'); return; }
    resetMsg(); setLoadingAction('fullInfo');
    try {
      const res = await metersService.getMeterFullInfo(meterId);
      setFullInfoData(res.data);
      setShowFullInfoModal(true);
    } catch (e) { setError(e?.response?.data?.detail?.message || 'Erreur'); }
    finally { setLoadingAction(null); }
  };
  // ── Relevé à distance
  const handleRead = async () => {
    if (!meterId || !needDcu()) return;
    resetMsg(); setLoadingAction('read'); setReadingData(null);
    try {
      const res = await metersService.remotelyRead(meterId);
      if (!res?.data?.success) { setError(res?.data?.message || 'Erreur relevé'); return; }
      setReadingData(res.data.reading);
      setShowReadingModal(true);
    } catch (e) { setError(e?.response?.data?.detail?.message || 'Erreur serveur'); }
    finally { setLoadingAction(null); }
  };

  // ── Infos client STRON
  const handleCustomerInfo = async () => {
    if (!meterId) { setError('Entrez un numéro de compteur'); return; }
    resetMsg(); setLoadingAction('customer');
    try {
      const res = await metersService.queryCustomerInfo(meterId);
      if (!res?.data?.success) { setError(res?.data?.message || 'Client introuvable'); return; }
      setCustomerData(res.data.customer_info);
      setShowCustomerModal(true);
    } catch (e) { setError(e?.response?.data?.detail?.message || 'Erreur'); }
    finally { setLoadingAction(null); }
  };

  // ── Historique crédit compteur
  const handleMeterCredit = async () => {
    if (!meterId) { setError('Entrez un numéro de compteur'); return; }
    resetMsg(); setLoadingAction('credit'); setCreditData(null);
    try {
      const res = await metersService.getMeterCreditHistoryLocal(meterId);
      if (!res?.data?.success) { setError(res?.data?.message || 'Erreur'); return; }
      setCreditData(res.data.records || []);
      setShowCreditModal(true);
    } catch (e) { setError(e?.response?.data?.detail?.message || 'Erreur'); }
    finally { setLoadingAction(null); }
  };

  // ── Effacer crédit distance
  const handleClearCredit = async () => {
    if (!meterId || !needDcu()) return;
    resetMsg(); setLoadingAction('clearCredit');
    try {
      const res = await metersService.clearCreditRemotely(meterId);
      if (!res?.data?.success) { setError(res?.data?.message || 'Erreur'); return; }
      handleActionResult(res, 'Effacer Crédit à Distance');
      loadHistory();
    } catch (e) { setError(e?.response?.data?.detail?.message || "Compteur hors ligne ?"); }
    finally { setLoadingAction(null); }
  };

  // ── Effacer tamper distance
  const handleClearTamper = async () => {
    if (!meterId || !needDcu()) return;
    resetMsg(); setLoadingAction('clearTamper');
    try {
      const res = await metersService.clearTamperRemotely(meterId);
      if (!res?.data?.success) { setError(res?.data?.message || 'Erreur'); return; }
      handleActionResult(res, 'Effacer Sabotage à Distance');
      loadHistory();
    } catch (e) { setError(e?.response?.data?.detail?.message || "Compteur hors ligne ?"); }
    finally { setLoadingAction(null); }
  };

  // ── Bloquer

  const handleBlock = async () => {
    if (!meterId || !needDcu()) return;
    resetMsg(); setLoadingAction('block'); startElapsedTimer();
    try {
      const res = await metersService.blockMeterRemote(meterId, 'Blocage à distance');
      if (!res?.data?.success) { setError(res?.data?.message || 'Erreur'); return; }
      setMeterBlocked(true);
      handleActionResult(res, 'Blocage Compteur');
      loadHistory();
    } catch (e) { setError(e?.response?.data?.detail?.message || "Compteur hors ligne ?"); }
    finally { setLoadingAction(null); stopElapsedTimer(); }
  };
  // ── Débloquer
  const handleUnblock = async () => {
    if (!meterId || !needDcu()) return;
    resetMsg(); setLoadingAction('unblock');
    try {
      const res = await metersService.unblockMeterRemote(meterId, 'Déblocage à distance');
      if (!res?.data?.success) { setError(res?.data?.message || 'Erreur'); return; }
      setMeterBlocked(false);
      handleActionResult(res, 'Déblocage Compteur');
      loadHistory();
    } catch (e) { setError(e?.response?.data?.detail?.message || "Compteur hors ligne ?"); }
    finally { setLoadingAction(null); }
  };

  // ── Recharge distance
  const handleVending = async () => {
    if (!meterId || !needDcu()) return;
    if (!vendAmount || parseFloat(vendAmount) <= 0) { setError('Montant invalide'); return; }
    resetMsg(); setLoadingAction('vending');
    try {
      const res = await metersService.vendingMeterRemotely(meterId, parseFloat(vendAmount), true);
      if (!res?.data?.success) { setError(res?.data?.message || 'Erreur'); return; }
      setShowVendingModal(false);
      if (res.data.token) {
        handleActionResult(res, `Recharge à Distance (${vendAmount} kWh)`);
      } else {
        setSuccess(`✅ Recharge ${vendAmount} kWh envoyée — vérifiez le crédit pour confirmer`);
      }
      setVendAmount('');
      loadHistory();
    } catch (e) { setError(e?.response?.data?.detail?.message || "Compteur hors ligne ?"); }
    finally { setLoadingAction(null); }
  };

  // ── Renvoi token
  const handleSendToken = async () => {
    if (!meterId || !sendTokenVal) { setError('Entrez un token'); return; }
    resetMsg(); setLoadingAction('sendToken');
    try {
      const res = await metersService.vendingSendToken(meterId, sendTokenVal);
      if (!res?.data?.success) { setError(res?.data?.message || 'Erreur'); return; }
      setSuccess('✅ Token renvoyé au compteur');
      setSendTokenVal(''); setShowSendTokenModal(false);
      loadHistory();
    } catch (e) { setError(e?.response?.data?.detail?.message || 'Erreur'); }
    finally { setLoadingAction(null); }
  };

  // ── Bascule prépayé/postpayé — ✅ génère un token, désormais affiché via handleActionResult
  const handleSwitch = async (type) => {
    if (!meterId) return;
    resetMsg(); setLoadingAction('switch');
    try {
      const res = type === 'prepaid'
        ? await metersService.switchToPrepaid(meterId, '')
        : await metersService.switchToPostpaid(meterId, '');
      if (!res?.data?.success) { setError(res?.data?.message || 'Erreur'); return; }
      setShowSwitchModal(false);
      handleActionResult(res, type === 'prepaid' ? 'Bascule Prépayé' : 'Bascule Postpayé');
      loadHistory();
    } catch (e) { setError(e?.response?.data?.detail?.message || 'Erreur'); }
    finally { setLoadingAction(null); }
  };

  // ── Consommation journalière
  const handleConso = async () => {
    if (!consoDate) { setError('Choisissez une date'); return; }
    resetMsg(); setLoadingAction('conso'); setConsoData(null);
    try {
      const res = await metersService.queryDailyConsumption(meterId, consoDate, consoPhase);
      if (!res?.data?.success) { setError(res?.data?.message || 'Erreur'); return; }
      setConsoData(res.data.daily_data || []);
    } catch (e) { setError(e?.response?.data?.detail?.message || 'Erreur'); }
    finally { setLoadingAction(null); }
  };

  // ── Reconfigurer concentrateur
  const handleRemoteSetDcu = async () => {
    if (!dcuId) { setError('Aucun DCU associé'); return; }
    if (dcuOnline === false) { setError('⚠️ Le concentrateur est hors ligne — reconfiguration impossible pour le moment.'); return; }
    resetMsg(); setLoadingAction('setDcu');
    try {
      const res = await metersService.remotelySetConcentrator(dcuId);
      if (!res?.data?.success) { setError(res?.data?.message || 'Erreur'); return; }
      setSuccess(`✅ Concentrateur ${dcuId} reconfiguré`);
      loadHistory();
    } catch (e) { setError(e?.response?.data?.detail?.message || 'Erreur'); }
    finally { setLoadingAction(null); }
  };

  const noMeter = !meterId;
  const noMeterNoDcu = !meterId || !dcuId;
  const dcuKnownOffline = dcuOnline === false;

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="bg-gradient-to-r from-indigo-600 to-blue-700 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center gap-3">
          <Satellite className="w-8 h-8" />
          <div>
            <h1 className="text-2xl font-bold">Contrôle à Distance</h1>
            <p className="text-indigo-100 text-sm mt-1">
              Opérations push sur le réseau AMI STRON via concentrateur (DCU)
            </p>
          </div>
        </div>
      </div>

      {/* ── Messages ── */}
      {error && (
        <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1"><p className="text-sm text-red-700 whitespace-pre-wrap">{error}</p></div>
          <button onClick={() => setError('')}><X className="w-5 h-5 text-red-400" /></button>
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 border-l-4 border-green-500 rounded-lg flex items-start gap-3">
          <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-800 flex-1">{success}</p>
          <button onClick={() => setSuccess('')}><X className="w-5 h-5 text-green-400" /></button>
        </div>
      )}
      {loadingAction && elapsedSeconds > 3 && (
        <div className="p-3 bg-blue-50 border-l-4 border-blue-400 rounded-lg flex items-center gap-2 text-sm text-blue-700">
          <Loader className="w-4 h-4 animate-spin" />
          Opération en cours sur le réseau AMI ({elapsedSeconds}s)… cela peut prendre jusqu'à 45s.
        </div>
      )}

      {/* ── Sélection compteur + DCU ── */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3">Sélectionner un compteur *</label>
        <input
          type="text"
          value={meterId}
          onChange={e => setMeterId(e.target.value)}
          placeholder="Ex: 58102588256"
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg font-mono mb-4"
        />

        {/* Bloc DCU */}
        {meterId && (
          <div className={`rounded-lg border-2 p-4 transition-colors ${loadingDcu ? 'bg-gray-50 border-gray-200' :
              dcuId ? (dcuOnline ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200') :
                'bg-red-50 border-red-200'
            }`}>
            {loadingDcu ? (
              <p className="text-sm text-gray-500 flex items-center gap-2"><Loader className="w-4 h-4 animate-spin" /> Recherche du concentrateur...</p>
            ) : dcuId && !editingDcu ? (
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <Radio className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-semibold text-gray-800">DCU : <span className="font-mono">{dcuId}</span></span>
                  {dcuOnline === true && <span className="flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full"><Wifi className="w-3 h-3" /> En ligne</span>}
                  {dcuOnline === false && <span className="flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-100 px-2 py-0.5 rounded-full"><WifiOff className="w-3 h-3" /> Hors ligne</span>}
                  {meterBlocked === true && <span className="flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-100 px-2 py-0.5 rounded-full"><Lock className="w-3 h-3" /> Bloqué</span>}
                  {meterBlocked === false && <span className="flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full"><Unlock className="w-3 h-3" /> Actif</span>}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => { setDcuDetails(dcuDetails); setShowDcuModal(true); }} title="Détails DCU" className="p-1.5 text-gray-400 hover:text-indigo-600 rounded-lg hover:bg-white">
                    <Info className="w-4 h-4" />
                  </button>
                  <button onClick={() => checkDcu(meterId)} title="Rafraîchir" className="p-1.5 text-gray-400 hover:text-indigo-600 rounded-lg hover:bg-white">
                    <RefreshCw className="w-4 h-4" />
                  </button>
                  <button onClick={() => { setDcuInput(dcuId); setEditingDcu(true); }} className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-indigo-700 bg-indigo-100 hover:bg-indigo-200 rounded-lg">
                    <Pencil className="w-3.5 h-3.5" /> Modifier
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col md:flex-row gap-3 items-stretch">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-red-800 mb-2">
                    {dcuId ? '✏️ Modifier le concentrateur' : '⚠️ Aucun concentrateur associé à ce compteur'}
                  </p>
                  <input
                    type="text"
                    value={dcuInput}
                    onChange={e => setDcuInput(e.target.value)}
                    placeholder="ID du concentrateur (DCU)"
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleAssociateDcu}
                    disabled={!dcuInput || loadingAction === 'dcu'}
                    className="px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-40 flex items-center gap-2"
                  >
                    {loadingAction === 'dcu' ? <Loader className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                    {dcuId ? 'Enregistrer' : 'Associer'}
                  </button>
                  {dcuId && <button onClick={() => setEditingDcu(false)} className="px-3 py-2 text-sm text-gray-500 bg-gray-100 rounded-lg hover:bg-gray-200">Annuler</button>}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ✅ Avertissement explicite si le DCU est hors ligne : évite de cliquer et d'attendre pour rien */}
        {dcuKnownOffline && (
          <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-orange-800">
              Le concentrateur est hors ligne : les opérations push (relevé, blocage, recharge, effacement...) échoueront après un long délai. Attendez qu'il repasse en ligne ou rafraîchissez son état avant d'agir.
            </p>
          </div>
        )}
      </div>

      {/* ── Grille d'actions ── */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {/* Lecture */}
        <h2 className="text-base font-bold text-gray-900 mb-4">📡 Lecture & Informations</h2>
        <PermissionGuard permission={PERMISSIONS.VIEW_REMOTE_CONTROL}>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
            <ActionCard icon={Eye} title="Relevé à Distance" color="blue" onClick={handleRead} disabled={noMeterNoDcu} loading={loadingAction === 'read'} />
            <ActionCard icon={DollarSign} title="Historique Crédit Compteur" color="green" onClick={handleMeterCredit} disabled={noMeter} loading={loadingAction === 'credit'} />
            <ActionCard icon={TrendingUp} title="Consommation Journalière" color="orange" onClick={() => setShowConsoModal(true)} disabled={noMeter} />
            <ActionCard icon={Info} title="Infos Complètes" color="blue" onClick={handleFullInfo} disabled={noMeter} loading={loadingAction === 'fullInfo'} />
          </div>
        </PermissionGuard>
        {/* Opérations push */}
        <h2 className="text-base font-bold text-gray-900 mb-4">⚡ Opérations Push (sans token)</h2>
        <PermissionGuard permission={PERMISSIONS.EXECUTE_REMOTE_ACTIONS}>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
            <ActionCard icon={DollarSign} title="Effacer Crédit à Distance" color="green" onClick={handleClearCredit} disabled={noMeterNoDcu} loading={loadingAction === 'clearCredit'} />
            <ActionCard icon={AlertCircle} title="Effacer Sabotage à Distance" color="orange" onClick={handleClearTamper} disabled={noMeterNoDcu} loading={loadingAction === 'clearTamper'} />
            <ActionCard icon={Zap} title="Recharge à Distance" color="blue" onClick={() => setShowVendingModal(true)} disabled={noMeterNoDcu} />
            <ActionCard icon={Send} title="Renvoyer un Token" color="indigo" onClick={() => setShowSendTokenModal(true)} disabled={noMeter} />
          </div>
        </PermissionGuard>

        {/* Alimentation & Config */}
        <h2 className="text-base font-bold text-gray-900 mb-4">🔌 Alimentation & Configuration</h2>
        <PermissionGuard permission={PERMISSIONS.EXECUTE_REMOTE_ACTIONS}>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <ActionCard icon={Lock} title="Bloquer Compteur" color="red" onClick={handleBlock} disabled={noMeterNoDcu} loading={loadingAction === 'block'} />
            <ActionCard icon={Unlock} title="Débloquer Compteur" color="gray" onClick={handleUnblock} disabled={noMeterNoDcu} loading={loadingAction === 'unblock'} />
            <ActionCard icon={ToggleLeft} title="Bascule Prépayé / Postpayé" color="blue" onClick={() => setShowSwitchModal(true)} disabled={noMeter} />
            <ActionCard icon={Settings} title="Reconfigurer Concentrateur" color="indigo" onClick={handleRemoteSetDcu} disabled={!dcuId || noMeter} loading={loadingAction === 'setDcu'} />
          </div>
        </PermissionGuard>
      </div>

      {/* ── Historique ── */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <History className="w-6 h-6 text-indigo-600" />
            <h2 className="text-lg font-bold text-gray-900">Historique des Opérations à Distance</h2>
          </div>
          <button onClick={loadHistory} disabled={historyLoading}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2 text-sm">
            {historyLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Filter className="w-4 h-4" />} Actualiser
          </button>
        </div>

        {/* Filtres */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <input type="text" placeholder="Filtrer par compteur" value={historyFilter.meter_id}
            onChange={e => setHistoryFilter({ ...historyFilter, meter_id: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm" />
          <select value={historyFilter.status}
            onChange={e => setHistoryFilter({ ...historyFilter, status: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm">
            <option value="">Tous les statuts</option>
            <option value="success">Réussi</option>
            <option value="failed">Échoué</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                {['Date', 'Compteur', 'Opération', 'Utilisateur', 'Token', 'Statut', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedHistory.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-10 text-gray-400">
                  <History className="w-10 h-10 text-gray-200 mx-auto mb-2" /><p className="text-sm">Aucune opération</p>
                </td></tr>
              ) : paginatedHistory.map(op => (
                <tr key={op.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {new Date(op.created_at).toLocaleString('fr-FR')}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-900">{op.meter_id}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{OP_LABELS[op.operation_type] || op.operation_type}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{op.username}</td>
                  <td className="px-4 py-3 text-xs font-mono text-gray-700">{op.token || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${op.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {op.status === 'success' ? '✓ Réussi' : '✗ Échoué'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => { setSelectedOp(op); setShowDetailModal(true); }}
                      className="p-1 text-indigo-600 hover:bg-indigo-50 rounded" title="Voir détails">
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPages > 1 && (
            <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={history.length}
              itemsPerPage={ITEMS_PER_PAGE} onPageChange={setCurrentPage} />
          )}
        </div>
      </div>

      {/* ═══════════════════ MODALS ═══════════════════ */}

      {/* ✅ NOUVEAU : Modal Token — identique au comportement du Centre Avancé */}
      {showTokenModal && tokenData && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-green-600 to-green-700 p-6 rounded-t-2xl text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                    <Check className="w-8 h-8" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Token généré avec succès !</h2>
                    <p className="text-green-100 text-sm mt-1">Compteur: {tokenData.meter_id}</p>
                  </div>
                </div>
                <button onClick={() => setShowTokenModal(false)} className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition">
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-600 font-semibold mb-1">Action effectuée</p>
                  <p className="text-lg font-bold text-blue-900">{tokenData.action}</p>
                </div>
                {tokenData.amount && (
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-600 font-semibold mb-1">Montant</p>
                    <p className="text-lg font-bold text-blue-900">{tokenData.amount} kWh</p>
                  </div>
                )}
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 col-span-2">
                  <p className="text-sm text-gray-600 font-semibold mb-1">Généré le</p>
                  <p className="text-base font-mono text-gray-900">{tokenData.generation_time}</p>
                </div>
              </div>

              <div className="relative">
                <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  Token
                </label>
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl p-6">
                  <div ref={tokenRef} className="text-center font-mono text-2xl md:text-3xl font-bold text-blue-900 break-all tracking-wider select-all p-4 bg-white bg-opacity-60 rounded-lg">
                    {tokenData.token}
                  </div>
                </div>
                {tokenData.message && (
                  <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800"><strong>Note:</strong> {tokenData.message}</p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-3">
                <button onClick={copyToken} className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold">
                  <Copy size={20} /> Copier
                </button>
                <button onClick={printToken} className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold">
                  <Printer size={20} /> Imprimer
                </button>
                <button onClick={downloadToken} className="flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold">
                  <Download size={20} /> Télécharger
                </button>
              </div>

              <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-orange-800">
                    <p className="font-semibold mb-1">⚠️ Important</p>
                    <p>Ce token est à usage <strong>unique</strong>. Conservez-le en lieu sûr.</p>
                  </div>
                </div>
              </div>

              <button onClick={() => setShowTokenModal(false)} className="w-full px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Relevé à distance */}
      {showReadingModal && (
        <Modal title="📊 Relevé à Distance" onClose={() => setShowReadingModal(false)} color="blue">
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Radio className="w-4 h-4" />
              <span>Compteur <span className="font-mono font-semibold text-gray-700">{meterId}</span></span>
            </div>

            {typeof readingData === 'object' && readingData !== null ? (
              <DailyMeterReadingCard data={readingData} />
            ) : (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 text-center">
                <Activity className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-blue-900 break-all">{String(readingData)}</p>
                <p className="text-xs text-blue-500 mt-2">Valeur brute renvoyée par le compteur</p>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Infos client */}
      {showCustomerModal && customerData && (
        <Modal title="👤 Infos Client (STRON)" onClose={() => setShowCustomerModal(false)} color="teal">
          <div className="space-y-1.5 text-sm max-h-72 overflow-y-auto">
            {Object.entries(customerData).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3 border-b border-gray-100 pb-1.5">
                <span className="text-gray-500 shrink-0">{k}</span>
                <span className="text-gray-900 font-medium text-right">{String(v)}</span>
              </div>
            ))}
          </div>
        </Modal>
      )}

      {/* Infos DCU */}
      {showDcuModal && dcuDetails && (
        <Modal title="🛰️ Infos Concentrateur" onClose={() => setShowDcuModal(false)} color="indigo">
          <div className="space-y-1.5 text-sm">
            {Object.entries(dcuDetails).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3 border-b border-gray-100 pb-1.5">
                <span className="text-gray-500 shrink-0">{k}</span>
                <span className="text-gray-900 font-medium text-right">{String(v)}</span>
              </div>
            ))}
          </div>
        </Modal>
      )}
      {/* Historique crédit */}
      {showCreditModal && (
        <Modal title="💰 Historique Crédit Compteur" onClose={() => setShowCreditModal(false)} color="blue">
          <div className="space-y-3 max-h-72 overflow-y-auto">
            {(!creditData || creditData.length === 0) ? (
              <p className="text-gray-400 text-center py-6">Aucun enregistrement dans l'application</p>
            ) : creditData.map((r, i) => (
              <div key={i} className="bg-gray-50 rounded-lg p-3 text-sm space-y-0.5 border border-gray-100">
                <div className="flex justify-between items-start">
                  <span className="font-semibold text-gray-800">{r.type_label}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${r.status === 'success' || r.status === 'completed' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'
                    }`}>{r.status}</span>
                </div>
                {r.token && <p><span className="font-semibold">Token :</span> <span className="font-mono text-xs">{r.token}</span></p>}
                {r.amount != null && <p><span className="font-semibold">Montant :</span> {r.amount} FCFA</p>}
                {r.kwh != null && <p><span className="font-semibold">Énergie :</span> {r.kwh} kWh</p>}
                {r.username && <p><span className="font-semibold">Par :</span> {r.username}</p>}
                <p className="text-xs text-gray-400">{new Date(r.date).toLocaleString('fr-FR')}</p>
              </div>
            ))}
          </div>
        </Modal>
      )}



      {/* Consommation journalière */}
      {showConsoModal && (
<Modal title="📈 Consommation Journalière" onClose={() => { setShowConsoModal(false); setConsoData(null); }} color="blue">
            <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Date</label>
                <input type="date" value={consoDate} onChange={e => setConsoDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Type compteur</label>
                <select value={consoPhase} onChange={e => setConsoPhase(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                  <option value="single">Monophasé</option>
                  <option value="three">Triphasé</option>
                  <option value="gas">Gaz</option>
                  <option value="water">Eau</option>
                </select>
              </div>
            </div>
            <button onClick={handleConso} disabled={!consoDate || loadingAction === 'conso'}
              className="w-full bg-blue-600 text-white py-2 rounded-lg font-semibold text-sm hover:bg-blue-700 disabled:opacity-40 flex items-center justify-center gap-2">
              {loadingAction === 'conso' ? <Loader className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Rechercher
            </button>
            {consoData !== null && (
              <div className="space-y-3 max-h-[28rem] overflow-y-auto">
                {consoData.length === 0 ? (
                  <div className="text-center py-8">
                    <TrendingUp className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">Aucune donnée pour cette date</p>
                  </div>
                ) : consoData.map((d, i) => (
                  <DailyMeterReadingCard key={i} data={d} />
                ))}
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Recharge distance */}
      {showVendingModal && (
        <Modal title="⚡ Recharge à Distance" onClose={() => { setShowVendingModal(false); setVendAmount(''); }} color="blue">
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Montant (kWh)</label>
              <input type="number" value={vendAmount} onChange={e => setVendAmount(e.target.value)}
                placeholder="Ex: 50"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-lg font-mono" />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              ⚠️ Recharge envoyée directement — aucun token à saisir. Vérifiez le crédit pour confirmer.
            </div>
            <button onClick={handleVending} disabled={!vendAmount || loadingAction === 'vending'}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-40 flex items-center justify-center gap-2">
              {loadingAction === 'vending' ? <Loader className="w-5 h-5 animate-spin" /> : <Zap className="w-5 h-5" />} Envoyer la Recharge
            </button>
          </div>
        </Modal>
      )}

      {/* Renvoi token */}
      {showSendTokenModal && (
        <Modal title="📤 Renvoyer un Token" onClose={() => { setShowSendTokenModal(false); setSendTokenVal(''); }} color="indigo">
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Token à renvoyer</label>
              <input type="text" value={sendTokenVal} onChange={e => setSendTokenVal(e.target.value)}
                placeholder="Ex: 1234 5678 9012 3456 7890"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono" />
            </div>
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-sm text-indigo-800">
              💡 Utilisez ceci si un token a déjà été généré (ex: via Bascule Prépayé/Postpayé) mais n'a pas été reçu par le compteur.
            </div>
            <button onClick={handleSendToken} disabled={!sendTokenVal || loadingAction === 'sendToken'}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-40 flex items-center justify-center gap-2">
              {loadingAction === 'sendToken' ? <Loader className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} Envoyer
            </button>
          </div>
        </Modal>
      )}

      {/* Bascule prépayé/postpayé */}
      {showSwitchModal && (
        <Modal title="🔄 Bascule Mode Facturation" onClose={() => setShowSwitchModal(false)} color="blue">
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Compteur : <span className="font-mono font-semibold">{meterId}</span></p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              ⚠️ Cette opération change le mode de facturation du compteur. Un token de confirmation sera généré et affiché.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => handleSwitch('prepaid')} disabled={loadingAction === 'switch'}
                className="py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-40 flex items-center justify-center gap-2 text-sm">
                {loadingAction === 'switch' ? <Loader className="w-4 h-4 animate-spin" /> : <ToggleLeft className="w-4 h-4" />} → Prépayé
              </button>
              <button onClick={() => handleSwitch('postpaid')} disabled={loadingAction === 'switch'}
                className="py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-40 flex items-center justify-center gap-2 text-sm">
                {loadingAction === 'switch' ? <Loader className="w-4 h-4 animate-spin" /> : <ToggleLeft className="w-4 h-4" />} → Postpayé
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Détail opération */}
      {showDetailModal && selectedOp && (
        <Modal title="📋 Détails Opération" onClose={() => setShowDetailModal(false)} color="blue">
          <div className="space-y-3 text-sm">
            {[
              ['Compteur', selectedOp.meter_id],
              ['Opération', OP_LABELS[selectedOp.operation_type] || selectedOp.operation_type],
              ['Statut', selectedOp.status === 'success' ? '✓ Réussi' : '✗ Échoué'],
              ['Utilisateur', selectedOp.username],
              ['Date', new Date(selectedOp.created_at).toLocaleString('fr-FR')],
              ['Raison', selectedOp.reason],
              ['Token', selectedOp.token],
            ].filter(([, v]) => v).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3 border-b border-gray-100 pb-1.5">
                <span className="text-gray-500">{k}</span>
                <span className="text-gray-900 font-medium text-right font-mono text-xs break-all">{v}</span>
              </div>
            ))}
            {selectedOp.token && (
              <button onClick={() => navigator.clipboard.writeText(selectedOp.token)}
                className="w-full mt-2 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 flex items-center justify-center gap-2">
                <Copy className="w-4 h-4" /> Copier le token
              </button>
            )}
          </div>
        </Modal>
      )}

      {showFullInfoModal && fullInfoData && (
        <Modal title="📋 Informations Complètes du Compteur" onClose={() => setShowFullInfoModal(false)} color="blue">
          <div className="space-y-5 text-sm max-h-[75vh] overflow-y-auto">

            {/* Client — notre base de données */}
            <div>
              <h4 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <User className="w-4 h-4 text-indigo-600" /> Client
              </h4>
              {fullInfoData.db_customer ? (
                <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
                  <Row label="Nom complet" value={fullInfoData.db_customer.full_name} />
                  <Row label="ID Client" value={fullInfoData.db_customer.customer_id} />
                  <Row label="Téléphone" value={fullInfoData.db_customer.phone} />
                  {fullInfoData.db_customer.phone2 && <Row label="Téléphone 2" value={fullInfoData.db_customer.phone2} />}
                  <Row label="Adresse" value={fullInfoData.db_customer.address} />
                  <Row label="Zone" value={fullInfoData.db_customer.zone} />
                  <Row label="Statut client" value={fullInfoData.db_customer.is_active ? '✅ Actif' : '🚫 Bloqué'} />
                </div>
              ) : (
                <p className="text-blue-600 text-xs">Aucun client associé à ce compteur dans notre base</p>
              )}
            </div>

            {/* Compteur — notre base de données */}
            {fullInfoData.db_meter && (
              <div>
                <h4 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-green-600" /> Compteur
                </h4>
                <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                  <Row label="Solde actuel" value={`${fullInfoData.db_meter.current_balance.toFixed(2)} kWh`} />
                  <Row label="Tarif" value={`${fullInfoData.db_meter.tariff} F CFA / kWh`} />
                  <Row label="Statut compteur" value={fullInfoData.db_meter.status} />
                  <Row label="Adresse d'installation" value={fullInfoData.db_meter.installation_address} />
                  {fullInfoData.db_meter.block_reason && <Row label="Raison du blocage" value={fullInfoData.db_meter.block_reason} />}
                </div>
              </div>
            )}
            {/* État Live du Compteur — Voltage/Current/Power/Relay/Cover */}
            <div>
  <h4 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
    <Zap className="w-4 h-4 text-blue-600" /> État Live du Compteur (STRON)
  </h4>
  {fullInfoData.device_state ? (
    <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
      <Row label="Tension" value={fullInfoData.device_state.Voltage} />
      <Row label="Courant" value={fullInfoData.device_state.Current} />
      <Row label="Puissance" value={fullInfoData.device_state.Power} />
      <Row label="Montant total acheté" value={fullInfoData.device_state.Cumulative_Purchased_Amount} />
      <Row label="Solde restant" value={fullInfoData.device_state.Residual_Amount} />
      <Row label="Consommation totale" value={fullInfoData.device_state.Cumulative_Total_Consumption} />
      <Row label="Type de compteur" value={fullInfoData.device_state.MeterType} />
      <Row label="État relais" value={fullInfoData.device_state.RelayState} />
      <Row label="État capot" value={fullInfoData.device_state.CoverState} />
    </div>
  ) : (
    <p className="text-blue-600 text-xs">
      {fullInfoData.device_state_error === 'TIMEOUT'
        ? 'Compteur/DCU hors ligne ou trop lent à répondre (timeout)'
        : "Aucun relevé disponible pour aujourd'hui"}
    </p>
  )}
</div>
            {/* Contrat / Tarif — STRON */}
            <div>
              <h4 className="font-bold text-gray-800 mb-2">📄 Contrat & Tarif (STRON)</h4>
              {fullInfoData.contract_info ? (
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <Row label="Société" value={fullInfoData.contract_info.Company_name} />
                  <Row label="Type de compteur" value={fullInfoData.contract_info.Meter_type} />
                  <Row label="Total payé (historique STRON)" value={`${Math.round(parseFloat(fullInfoData.contract_info.Total_paid)).toLocaleString('fr-FR')} F CFA`} />
                  <Row label="Total consommé" value={`${fullInfoData.contract_info.Total_unit} kWh`} />
                  <Row label="Nombre de recharges" value={fullInfoData.contract_info.Total_times} />
                  <Row label="Prix unitaire" value={`${fullInfoData.contract_info.Price} ${fullInfoData.contract_info.Price_unit} / kWh`} />
                </div>
              ) : (
                <p className="text-orange-600 text-xs">{fullInfoData.contract_info_error || 'Indisponible'}</p>
              )}
            </div>

            {/* Recharges — STRON */}
            <div>
              <h4 className="font-bold text-gray-800 mb-2">🎫 Dernières Recharges (STRON)</h4>
              {(fullInfoData.credit_history || []).length === 0 ? (
                <p className="text-gray-400 text-xs">Aucun enregistrement</p>
              ) : (
                <div className="space-y-2">
                  {fullInfoData.credit_history.map((r, i) => (
                    <div key={i} className="bg-blue-50 rounded-lg p-3 border border-blue-100 text-xs">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-mono text-blue-900">{r.token}</span>
                        <span className="text-gray-500">{r.date}</span>
                      </div>
                      <div className="flex gap-4">
                        {r.montant_fcfa !== null ? (
                          <span className="font-semibold text-green-700">💰 Montant : {Math.round(r.montant_fcfa).toLocaleString('fr-FR')} F CFA</span>
                        ) : null}
                        {r.energie_kwh !== null ? (
                          <span className="font-semibold text-blue-700">⚡ Énergie : {r.energie_kwh.toFixed(2)} kWh</span>
                        ) : null}
                        {r.montant_fcfa === null && r.energie_kwh === null && (
                          <span className="text-gray-600">{r.montant_brut}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </Modal>
      )}
    </div>
  );
}

// ── composant Modal réutilisable ─────────────────────────────────────────────
const MODAL_COLORS = {
  blue: 'from-blue-600 to-blue-700',
  green: 'from-green-600 to-green-700',
  teal: 'from-teal-600 to-teal-700',
  orange: 'from-orange-600 to-orange-700',
  blue: 'from-blue-600 to-blue-700',
  indigo: 'from-indigo-600 to-indigo-700',
};
function Row({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-3 py-1">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span className="text-gray-900 font-medium sm:text-right break-words">{value}</span>
    </div>
  );
}

function Modal({ title, onClose, color = 'blue', children }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className={`bg-gradient-to-r ${MODAL_COLORS[color] || MODAL_COLORS.blue} p-5 rounded-t-2xl text-white flex items-center justify-between`}>
          <h2 className="text-lg font-bold">{title}</h2>
          <button onClick={onClose} className="text-white hover:bg-white hover:bg-opacity-20 p-1.5 rounded-lg">
            <X size={20} />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}